import dynamic from 'next/dynamic';
import type { GetServerSideProps, NextPage } from 'next';
import { useMedia } from '@stitch-fix/mode-react';
import type { infer as Infer } from 'zod';
import { serverLogger } from '../../utils/loggers/server';
import { FormContextProvider } from '../../components/Gifts/FormContext';
import { ModularPageWrapper } from '../../components/ModularPageWrapper';
import { modularPageWrapperSchema } from '../../components/ModularPageWrapper/schema';
import { getCachedEntryByUrl } from '../../utils/requests/contentstack/cachedRequests';
import { useGiftCardModules } from './useGiftCardModuleOverrides';
const BuyWizard = dynamic(() => import('../../components/Gifts/BuyWizard'), {
  ssr: false
});
const RedeemWizard = dynamic(() => import('../../components/Gifts/RedeemWizard'), {
  ssr: false
});
type GiftsPageProps = Infer<typeof modularPageWrapperSchema>;
export const getServerSideProps: GetServerSideProps<GiftsPageProps> = async () => {
  try {
    const entryResponse = await getCachedEntryByUrl({
      contentType: 'modular_page',
      url: '/gifts',
      schema: modularPageWrapperSchema,
      logger: serverLogger
    });
    return {
      props: {
        seoData: entryResponse.data.seoData,
        modules: entryResponse.data.modules
      }
    };
  } catch (error: unknown) {
    serverLogger({
      level: 'error',
      team: 'incentives',
      message: 'Required page data is undefined - /gifts',
      context: {
        error,
        page: '/gifts',
        severity: 'critical'
      }
    });
    throw new Error('Required data not provided to render /gifts page');
  }
};
const GiftsPage: NextPage<GiftsPageProps> = ({
  seoData,
  modules
}) => {
  const {
    modules: overriddenModules,
    activeWizard,
    setActiveWizard
  } = useGiftCardModules({
    modules
  });
  const isMobile = useMedia('(max-width: 900px)');
  const hideModules = activeWizard !== null;
  return <FormContextProvider>
      <ModularPageWrapper seoData={seoData} modules={hideModules ? [] : overriddenModules} forceHideHeaderFooter={hideModules} viewEventTrackingName="gift_card">
        {activeWizard === 'buy' && <BuyWizard isMobile={isMobile} onClick={() => setActiveWizard(null)} />}
        {activeWizard === 'redeem' && <RedeemWizard isMobile={isMobile} onClick={() => setActiveWizard(null)} />}
      </ModularPageWrapper>
    </FormContextProvider>;
};
export default GiftsPage;